// 3D viewport for the Freehand Draw workspace.
//
// The user draws directly on a solid unit-sphere. In "freeform" mode a
// pointer-down starts a stroke, pointer-move extends it, pointer-up finishes
// it. In "polyline" mode each click adds a corner; double-click closes.
// The Escape key cancels a WIP stroke. Committed panels render as translucent
// filled spherical polygons above the ball.

import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, ContactShadows, Html, Line } from '@react-three/drei';
import earcut from 'earcut';
import { useFreehand, renderedPerimeter } from '@/lib/freehandStore';
import { replicateStroke, findSnap, generateGridPoints, findGridSnap, pickBestSnap, buildShapePolygon, symmetryGuideAxes, SCALE_PIVOTS } from '@/lib/freehandGeometry';
import { projectDecal, densify2d } from '@/lib/svgImport';

const BALL_RADIUS = 1;
const PANEL_LIFT = 1.004;
const PREVIEW_LIFT = 1.014;

// Slerp two unit vectors.
function slerp(a, b, t) {
  const d = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const ang = Math.acos(d);
  if (ang < 1e-6) return [a[0], a[1], a[2]];
  const s = Math.sin(ang);
  const w1 = Math.sin((1 - t) * ang) / s;
  const w2 = Math.sin(t * ang) / s;
  return [a[0] * w1 + b[0] * w2, a[1] * w1 + b[1] * w2, a[2] * w1 + b[2] * w2];
}

function StudioLights() {
  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[6, 8, 5]} intensity={1.15} castShadow shadow-mapSize={[2048, 2048]} />
      <directionalLight position={[-6, -3, -4]} intensity={0.3} color="#a5c8ff" />
    </>
  );
}

// A wireframe globe grid to help the eye read curvature. Rendered separately
// from the drawing mesh so the raycaster only hits the drawing mesh.
function BallSurface({ opacity }) {
  return (
    <mesh scale={[1.001, 1.001, 1.001]} raycast={() => null}>
      <sphereGeometry args={[BALL_RADIUS, 32, 16]} />
      <meshBasicMaterial
        color="#E2E8F0"
        wireframe
        transparent
        opacity={0.24 * Math.max(opacity, 0.15)}
      />
    </mesh>
  );
}

// Dashed overlay showing every rotation axis of the active symmetry group.
// Each axis is a long dashed stick that passes through the ball origin so
// both poles stick out of the sphere surface and remain visible from any
// camera angle. Axes are hue-coded by rotation order: 2-fold cyan, 3-fold
// amber, 4-fold emerald, 5-fold pink. The lines render with depthWrite off
// so they softly blend with the mesh instead of fighting the depth buffer.
function AxisGuides({ mode, ringN }) {
  const axes = useMemo(() => symmetryGuideAxes(mode, ringN), [mode, ringN]);
  if (!axes.length) return null;
  const colourFor = (fold) => {
    if (fold >= 5) return '#EC4899'; // pink — 5-fold
    if (fold === 4) return '#10B981'; // emerald — 4-fold
    if (fold === 3) return '#F59E0B'; // amber — 3-fold
    return '#06B6D4';                 // cyan — 2-fold (and ring at low fold)
  };
  // Push the sticks slightly past the ball surface so they're clearly
  // visible on both poles. 1.18 gives roughly 0.18 ball-radii of "stick"
  // poking out on each side — enough to see from any orbit angle while
  // still staying inside the Canvas bounds.
  const R = 1.18;
  return (
    <group raycast={() => null}>
      {axes.map(({ axis, fold }, i) => {
        const p1 = [axis[0] * R, axis[1] * R, axis[2] * R];
        const p2 = [-axis[0] * R, -axis[1] * R, -axis[2] * R];
        return (
          <Line
            key={`axis-${i}`}
            points={[p1, p2]}
            color={colourFor(fold)}
            lineWidth={1.5}
            dashed
            dashSize={0.04}
            gapSize={0.02}
            transparent
            opacity={0.75}
            depthWrite={false}
          />
        );
      })}
    </group>
  );
}

// Inner "reference" sphere textured with a user-uploaded photo. Rendered at
// radius 0.996 (just under the outer ball) so lowering the outer ball's
// opacity makes the reference show through — perfect for tracing artwork.
function ReferenceBall({ image }) {
  const texture = useMemo(() => {
    if (!image?.dataUrl) return null;
    const loader = new THREE.TextureLoader();
    const t = loader.load(image.dataUrl);
    t.colorSpace = THREE.SRGBColorSpace;
    // Clamp edges so a scale < 1 (image smaller than the sphere UV) doesn't
    // tile — the outermost row of pixels just extends outward instead.
    t.wrapS = THREE.ClampToEdgeWrapping;
    t.wrapT = THREE.ClampToEdgeWrapping;
    return t;
  }, [image?.dataUrl]);
  // Free the WebGL texture when the dataUrl changes (or the ball unmounts) —
  // otherwise every re-uploaded reference photo leaks its GPU handle.
  useEffect(() => () => { if (texture) texture.dispose(); }, [texture]);
  // Update repeat/offset every render so the sliders react live without
  // rebuilding the texture. `scale > 1` zooms in (only the centre portion of
  // the image wraps the sphere); `scale < 1` shrinks the image and clamps
  // the border pixels around it.
  const sx = image?.scaleX ?? 1;
  const sy = image?.scaleY ?? 1;
  if (texture) {
    texture.repeat.set(1 / sx, 1 / sy);
    texture.offset.set((1 - 1 / sx) / 2, (1 - 1 / sy) / 2);
    texture.needsUpdate = true;
  }
  if (!texture) return null;
  return (
    <mesh rotation={[0, image.rotationY || 0, 0]} raycast={() => null}>
      <sphereGeometry args={[BALL_RADIUS * 0.996, 96, 64]} />
      <meshBasicMaterial map={texture} side={THREE.FrontSide} />
    </mesh>
  );
}

// Floating "P{n}" numeric label drawn just off the ball surface at each
// panel's centroid. Numbering is 1-based on the panels[] array order, which
// is the same order `getMesh().faces` uses — so the label on the ball
// matches the "P{n}" label on the exported SVG/DXF sheet. Uses drei's
// `<Html>` so the labels are always screen-space crisp and readable.
//
// Only labels whose centroid faces the camera are shown — rear-facing labels
// are hidden per-frame so the ball doesn't visually double-print when both
// front and back sit within the viewport.
function PanelNumbers({ panels }) {
  const LIFT = BALL_RADIUS * 1.08;
  // Precompute each label's centroid on the unit sphere so the useFrame loop
  // is a couple of dot products per panel and nothing more.
  const centroids = useMemo(() => panels.map((p) => {
    const pts = p.points;
    if (!pts || pts.length === 0) return null;
    let cx = 0, cy = 0, cz = 0;
    for (const v of pts) { cx += v[0]; cy += v[1]; cz += v[2]; }
    const l = Math.hypot(cx, cy, cz) || 1;
    return [cx / l, cy / l, cz / l];
  }), [panels]);
  const refs = useRef([]);
  useFrame(({ camera }) => {
    const cx = camera.position.x, cy = camera.position.y, cz = camera.position.z;
    const cLen = Math.hypot(cx, cy, cz) || 1;
    const dx = cx / cLen, dy = cy / cLen, dz = cz / cLen;
    // A label is considered "visible" if its outward normal has a positive
    // dot with the camera direction — i.e. it's on the near hemisphere. A
    // very small negative threshold hides labels right at the silhouette
    // where they'd flicker as the ball rotates.
    for (let i = 0; i < centroids.length; i++) {
      const c = centroids[i];
      const el = refs.current[i];
      if (!el || !c) continue;
      const facing = c[0] * dx + c[1] * dy + c[2] * dz > 0.08;
      // Toggle display rather than opacity so hidden labels don't intercept
      // pointer events and don't cost layout time.
      const desired = facing ? '' : 'none';
      if (el.style.display !== desired) el.style.display = desired;
    }
  });
  return (
    <group>
      {panels.map((p, i) => {
        const c = centroids[i];
        if (!c) return null;
        return (
          <Html
            key={p.id}
            position={[c[0] * LIFT, c[1] * LIFT, c[2] * LIFT]}
            center
            distanceFactor={6}
            zIndexRange={[10, 0]}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            <span
              ref={(el) => { refs.current[i] = el; }}
              data-testid={`freehand-panel-number-${i + 1}`}
              className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#0F172A] text-white text-[11px] font-semibold shadow-md ring-2 ring-white/90 whitespace-nowrap"
            >
              P{i + 1}
            </span>
          </Html>
        );
      })}
    </group>
  );
}

// Bright pink great-circle guides that follow the pointer. The vertical
// guide is the meridian through the hover point (great circle containing
// the Y axis and the hit point). The horizontal guide is the parallel at
// the same latitude (small circle at constant y). Rendered only while a
// drawing/edit tool is active.
const GUIDE_COLOR = '#EC4899';
const GUIDE_LIFT = BALL_RADIUS * 1.005;

function circleGeom(points) {
  const g = new THREE.BufferGeometry();
  const n = points.length;
  const verts = new Float32Array((n + 1) * 3);
  for (let i = 0; i < n; i++) {
    verts[3 * i]     = points[i][0];
    verts[3 * i + 1] = points[i][1];
    verts[3 * i + 2] = points[i][2];
  }
  verts[3 * n]     = points[0][0];
  verts[3 * n + 1] = points[0][1];
  verts[3 * n + 2] = points[0][2];
  g.setAttribute('position', new THREE.BufferAttribute(verts, 3));
  return g;
}

function GuideLines() {
  const showGuides = useFreehand((s) => s.showGuides);
  const hover = useFreehand((s) => s.hoverPoint);
  const tool = useFreehand((s) => s.tool);
  if (!showGuides || !hover) return null;
  if (tool !== 'freeform' && tool !== 'polyline' && tool !== 'edit') return null;

  const [hx, hy, hz] = hover;
  // Horizontal projection of hover onto XZ plane, normalised. Defines the
  // meridian direction.
  const hxzLen = Math.hypot(hx, hz);
  const dirX = hxzLen > 1e-4 ? hx / hxzLen : 1;
  const dirZ = hxzLen > 1e-4 ? hz / hxzLen : 0;
  // Meridian: cos(t)·[dirX,0,dirZ] + sin(t)·[0,1,0]  (a great circle)
  const segs = 128;
  const meridian = [];
  for (let i = 0; i < segs; i++) {
    const t = (2 * Math.PI * i) / segs;
    const c = Math.cos(t), s = Math.sin(t);
    meridian.push([dirX * c * GUIDE_LIFT, s * GUIDE_LIFT, dirZ * c * GUIDE_LIFT]);
  }
  // Parallel: small circle at constant y = hy, radius √(1-hy²)
  const r = Math.sqrt(Math.max(0, 1 - hy * hy));
  const parallel = [];
  for (let i = 0; i < segs; i++) {
    const t = (2 * Math.PI * i) / segs;
    parallel.push([r * Math.cos(t) * GUIDE_LIFT, hy * GUIDE_LIFT, r * Math.sin(t) * GUIDE_LIFT]);
  }
  return (
    <group>
      <line geometry={circleGeom(meridian)} raycast={() => null} renderOrder={3}>
        <lineBasicMaterial color={GUIDE_COLOR} transparent opacity={0.9} linewidth={2} depthTest={false} />
      </line>
      <line geometry={circleGeom(parallel)} raycast={() => null} renderOrder={3}>
        <lineBasicMaterial color={GUIDE_COLOR} transparent opacity={0.9} linewidth={2} depthTest={false} />
      </line>
    </group>
  );
}

// Small "+" markers at every grid intersection so the user can see the grid
// they'll snap to. Rendered as one LineSegments batch for performance.
// Special case: `axes` grid renders three continuous great-circle rings
// instead so users can trace panels perfectly along each cardinal axis.
function GridMarkers() {
  const gridType = useFreehand((s) => s.gridType);
  const gridDensity = useFreehand((s) => s.gridDensity);
  const gridOpacity = useFreehand((s) => s.gridOpacity);
  const geom = useMemo(() => {
    if (gridType === 'off') return null;
    if (gridType === 'axes') {
      // Three continuous great circles at radius 1.006. Each ring is drawn
      // as a closed LineLoop-style sequence packed into a single
      // LineSegments buffer (pairs of adjacent points on the ring).
      const segs = 128;
      const lift = 1.006;
      const positions = new Float32Array(3 * segs * 2 * 3);
      let idx = 0;
      const pushSeg = (a, b) => {
        positions[idx++] = a[0]; positions[idx++] = a[1]; positions[idx++] = a[2];
        positions[idx++] = b[0]; positions[idx++] = b[1]; positions[idx++] = b[2];
      };
      for (let i = 0; i < segs; i++) {
        const t0 = (2 * Math.PI * i) / segs;
        const t1 = (2 * Math.PI * (i + 1)) / segs;
        const c0 = Math.cos(t0) * lift, s0 = Math.sin(t0) * lift;
        const c1 = Math.cos(t1) * lift, s1 = Math.sin(t1) * lift;
        pushSeg([c0, s0, 0], [c1, s1, 0]); // XY-plane
        pushSeg([0, c0, s0], [0, c1, s1]); // YZ-plane
        pushSeg([c0, 0, s0], [c1, 0, s1]); // XZ-plane
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      return g;
    }
    const pts = generateGridPoints(gridType, gridDensity);
    if (!pts || !pts.length) return null;
    const armLen = 0.02; // arm half-length of each "+"
    const positions = new Float32Array(pts.length * 4 * 3); // 4 vertices (2 segments) per cross
    let idx = 0;
    for (const p of pts) {
      // Tangent basis at p.
      const up = Math.abs(p[1]) < 0.95 ? [0, 1, 0] : [1, 0, 0];
      let ex = [
        up[1] * p[2] - up[2] * p[1],
        up[2] * p[0] - up[0] * p[2],
        up[0] * p[1] - up[1] * p[0],
      ];
      const el = Math.hypot(ex[0], ex[1], ex[2]) || 1;
      ex = [ex[0] / el, ex[1] / el, ex[2] / el];
      const ey = [
        p[1] * ex[2] - p[2] * ex[1],
        p[2] * ex[0] - p[0] * ex[2],
        p[0] * ex[1] - p[1] * ex[0],
      ];
      const lift = 1.006;
      const cx = p[0] * lift, cy = p[1] * lift, cz = p[2] * lift;
      // Horizontal arm
      positions[idx++] = cx + ex[0] * armLen; positions[idx++] = cy + ex[1] * armLen; positions[idx++] = cz + ex[2] * armLen;
      positions[idx++] = cx - ex[0] * armLen; positions[idx++] = cy - ex[1] * armLen; positions[idx++] = cz - ex[2] * armLen;
      // Vertical arm
      positions[idx++] = cx + ey[0] * armLen; positions[idx++] = cy + ey[1] * armLen; positions[idx++] = cz + ey[2] * armLen;
      positions[idx++] = cx - ey[0] * armLen; positions[idx++] = cy - ey[1] * armLen; positions[idx++] = cz - ey[2] * armLen;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return g;
  }, [gridType, gridDensity]);
  // Dispose the buffer geometry when the grid config changes so we don't
  // leak on every "off → hex → tri" toggle.
  useEffect(() => () => { if (geom) geom.dispose(); }, [geom]);
  if (!geom) return null;
  return (
    <lineSegments geometry={geom} raycast={() => null} renderOrder={2}>
      <lineBasicMaterial
        color="#EC4899"
        transparent
        opacity={Math.max(0.15, gridOpacity)}
        linewidth={gridType === 'axes' ? 2 : 1}
      />
    </lineSegments>
  );
}


// Densify a spherical polygon by inserting slerp-interpolated points along
// every edge longer than `maxAngleRad`. Guarantees the triangulation hugs the
// sphere and the outline renders as smooth great-circle arcs instead of
// chords sinking into the ball.
function densifyPolygon(points, maxAngleRad = 0.15) {
  const n = points.length;
  const out = [];
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    out.push(a);
    const dp = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
    const ang = Math.acos(dp);
    if (ang <= maxAngleRad) continue;
    const steps = Math.ceil(ang / maxAngleRad);
    for (let k = 1; k < steps; k++) {
      out.push(slerp(a, b, k / steps));
    }
  }
  return out;
}

// Triangulate a spherical polygon by projecting into the tangent plane at its
// centroid, running earcut for robust concave/irregular support, then lifting
// each interior vertex back to the sphere at `lift` radius. Every triangle is
// additionally slerp-subdivided along its centroid→edge spokes so panels
// larger than a few degrees still hug the ball's curvature.
function panelGeometry(points, lift, spherized = true, holes = []) {
  const dense = densifyPolygon(points, 0.1);
  const n = dense.length;
  if (n < 3) return new THREE.BufferGeometry();
  // Densify each hole too so the triangulation follows the sphere.
  const denseHoles = (holes || []).map((h) => densifyPolygon(h, 0.1)).filter((h) => h.length >= 3);

  // Spherical centroid.
  const cSum = [0, 0, 0];
  for (const p of dense) {
    cSum[0] += p[0];
    cSum[1] += p[1];
    cSum[2] += p[2];
  }
  const cl = Math.hypot(cSum[0], cSum[1], cSum[2]) || 1;
  const ctr = [cSum[0] / cl, cSum[1] / cl, cSum[2] / cl];

  // Tangent-plane basis at the centroid.
  const up = Math.abs(ctr[1]) < 0.95 ? [0, 1, 0] : [1, 0, 0];
  let ex = [
    up[1] * ctr[2] - up[2] * ctr[1],
    up[2] * ctr[0] - up[0] * ctr[2],
    up[0] * ctr[1] - up[1] * ctr[0],
  ];
  const exL = Math.hypot(ex[0], ex[1], ex[2]) || 1;
  ex = [ex[0] / exL, ex[1] / exL, ex[2] / exL];
  const ey = [
    ctr[1] * ex[2] - ctr[2] * ex[1],
    ctr[2] * ex[0] - ctr[0] * ex[2],
    ctr[0] * ex[1] - ctr[1] * ex[0],
  ];

  // Project each point (outer + holes) into the tangent plane. `flat` holds
  // all rings concatenated for earcut; `holeIndices` marks where each hole
  // starts in `flat`.
  const allRings = [dense, ...denseHoles];
  let vertCount = 0;
  for (const r of allRings) vertCount += r.length;
  const flat = new Array(vertCount * 2);
  const holeIndices = [];
  let cursor = 0;
  for (let r = 0; r < allRings.length; r++) {
    if (r > 0) holeIndices.push(cursor);
    for (const p of allRings[r]) {
      const dp = p[0] * ctr[0] + p[1] * ctr[1] + p[2] * ctr[2];
      const scale = dp > 1e-6 ? 1 / dp : 1;
      flat[cursor * 2]     = p[0] * scale * ex[0] + p[1] * scale * ex[1] + p[2] * scale * ex[2];
      flat[cursor * 2 + 1] = p[0] * scale * ey[0] + p[1] * scale * ey[1] + p[2] * scale * ey[2];
      cursor++;
    }
  }

  let tris = earcut(flat, holeIndices.length ? holeIndices : null);
  if (!tris || !tris.length) {
    tris = [];
    for (let i = 1; i < n - 1; i++) tris.push(0, i, i + 1);
  }

  // Winding: reverse if earcut's triangles face inward in the tangent plane.
  let signed = 0;
  for (let i = 0; i < tris.length; i += 3) {
    const ax = flat[2 * tris[i]],     ay = flat[2 * tris[i] + 1];
    const bx = flat[2 * tris[i + 1]], by = flat[2 * tris[i + 1] + 1];
    const cx = flat[2 * tris[i + 2]], cy = flat[2 * tris[i + 2] + 1];
    signed += (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  }
  const flip = signed < 0;

  // Recursively subdivide each triangle whose longest edge exceeds
  // `maxEdgeRad` and lift every subdivided vertex onto the sphere. This is
  // what makes the panel visibly hug the ball rather than sag across chords.
  // When `spherized === false` we skip the subdivision and just use the raw
  // dense-boundary earcut result for a faceted look.
  const positions = [];
  const indices = [];

  const pushVertex = (v) => {
    positions.push(v[0] * lift, v[1] * lift, v[2] * lift);
    return positions.length / 3 - 1;
  };

  // Seed with the dense boundary vertices in order, then every hole ring
  // so `boundaryIdx[i]` maps directly to the earcut index i (outer 0..n-1
  // then hole 0 verts, hole 1 verts, ...).
  const boundaryIdx = [];
  for (const r of allRings) for (const p of r) boundaryIdx.push(pushVertex(p));

  const maxEdgeRad = 0.06; // ≈ 3.4° per edge
  const subdivide = (aIdx, bIdx, cIdx, depth) => {
    const A = [positions[3 * aIdx], positions[3 * aIdx + 1], positions[3 * aIdx + 2]];
    const B = [positions[3 * bIdx], positions[3 * bIdx + 1], positions[3 * bIdx + 2]];
    const C = [positions[3 * cIdx], positions[3 * cIdx + 1], positions[3 * cIdx + 2]];
    // Convert back to unit-sphere directions for angle math.
    const norm3 = (v) => {
      const l = Math.hypot(v[0], v[1], v[2]) || 1;
      return [v[0] / l, v[1] / l, v[2] / l];
    };
    const uA = norm3(A), uB = norm3(B), uC = norm3(C);
    const ang = (u, v) => Math.acos(Math.max(-1, Math.min(1, u[0] * v[0] + u[1] * v[1] + u[2] * v[2])));
    const eAB = ang(uA, uB);
    const eBC = ang(uB, uC);
    const eCA = ang(uC, uA);
    const longest = Math.max(eAB, eBC, eCA);
    if (!spherized || depth >= 5 || longest <= maxEdgeRad) {
      if (flip) indices.push(aIdx, cIdx, bIdx);
      else indices.push(aIdx, bIdx, cIdx);
      return;
    }
    // 4-way split by inserting midpoints on each edge, lifted to sphere.
    const mid = (u, v) => {
      const m = [u[0] + v[0], u[1] + v[1], u[2] + v[2]];
      return norm3(m);
    };
    const mAB = pushVertex(mid(uA, uB));
    const mBC = pushVertex(mid(uB, uC));
    const mCA = pushVertex(mid(uC, uA));
    subdivide(aIdx, mAB, mCA, depth + 1);
    subdivide(mAB, bIdx, mBC, depth + 1);
    subdivide(mCA, mBC, cIdx, depth + 1);
    subdivide(mAB, mBC, mCA, depth + 1);
  };

  for (let i = 0; i < tris.length; i += 3) {
    subdivide(boundaryIdx[tris[i]], boundaryIdx[tris[i + 1]], boundaryIdx[tris[i + 2]], 0);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geom.setIndex(new THREE.BufferAttribute(new Uint32Array(indices), 1));
  return geom;
}

function outlineGeometry(points, lift) {
  const dense = densifyPolygon(points, 0.05);
  const geom = new THREE.BufferGeometry();
  const n = dense.length;
  const verts = new Float32Array((n + 1) * 3);
  for (let i = 0; i < n; i++) {
    verts[3 * i]     = dense[i][0] * lift;
    verts[3 * i + 1] = dense[i][1] * lift;
    verts[3 * i + 2] = dense[i][2] * lift;
  }
  verts[3 * n]     = dense[0][0] * lift;
  verts[3 * n + 1] = dense[0][1] * lift;
  verts[3 * n + 2] = dense[0][2] * lift;
  geom.setAttribute('position', new THREE.BufferAttribute(verts, 3));
  return geom;
}

function CommittedPanels({ panels, onSelect, selectedId, onEnterManip }) {
  const spherized = useFreehand((s) => s.spherized);
  const tool = useFreehand((s) => s.tool);
  return (
    <group>
      {panels.map((p) => (
        <CommittedPanel
          key={p.id}
          panel={p}
          spherized={spherized}
          tool={tool}
          selectedId={selectedId}
          onSelect={onSelect}
          onEnterManip={onEnterManip}
        />
      ))}
    </group>
  );
}

// Per-panel wrapper that memoises its BufferGeometries and disposes them on
// cleanup. Without this, `panelGeometry()` and `outlineGeometry()` fire on
// every parent re-render (orbit, hover, symmetry change, …) and every fresh
// geometry leaked GPU + JS memory — the browser eventually kills the tab.
function CommittedPanel({ panel: p, spherized, tool, selectedId, onSelect, onEnterManip }) {
  const perim = renderedPerimeter(p);
  const holes = useMemo(
    () => (p.holes || []).map((h) => h.map((v) => {
      const l = Math.hypot(v[0], v[1], v[2]) || 1;
      return [v[0] / l, v[1] / l, v[2] / l];
    })),
    // renderedPerimeter() bakes p.smooth into the perim, so we key holes on
    // the raw hole array identity (they don't include smoothing).
    [p.holes],
  );
  // Deterministic per-panel radial jitter. Two freehand panels drawn to
  // overlap in 3D (or mirror-replicated near a symmetry plane) otherwise
  // sit at exactly `PANEL_LIFT` and Z-fight pixel-by-pixel, which shows up
  // as "furry" striations across whichever panel loses the depth test on
  // half its triangles. A sub-millimetre lift offset per id breaks the tie
  // without being visually detectable on the ball.
  const liftJitter = useMemo(() => {
    const id = String(p.id);
    let h = 0;
    for (let i = 0; i < id.length; i++) h = ((h * 31) + id.charCodeAt(i)) | 0;
    // 0..1 → 0..0.0012 lift (≈0.12% of ball radius)
    return ((h >>> 0) % 1000) / 1000 * 0.0012;
  }, [p.id]);
  const panelLift = PANEL_LIFT + liftJitter;
  // Keying on the perimeter payload + holes + spherized flag is sufficient;
  // JSON.stringify keeps this stable across renders where the actual values
  // haven't changed.
  const perimKey = useMemo(() => JSON.stringify(perim), [perim]);
  const holesKey = useMemo(() => JSON.stringify(holes), [holes]);
  const fillGeom = useMemo(
    () => panelGeometry(perim, panelLift, spherized, holes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perimKey, holesKey, spherized, panelLift],
  );
  const outlineGeom = useMemo(
    () => outlineGeometry(perim, panelLift + 0.001),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perimKey, panelLift],
  );
  const holeOutlineGeoms = useMemo(
    () => holes.map((h) => outlineGeometry(h, panelLift + 0.001)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [holesKey, panelLift],
  );
  // Dispose GPU/JS resources when any memoised geometry is replaced or the
  // panel unmounts. React runs the cleanup with the PREVIOUS closure, so we
  // capture each geometry there.
  useEffect(() => () => fillGeom.dispose(), [fillGeom]);
  useEffect(() => () => outlineGeom.dispose(), [outlineGeom]);
  useEffect(() => () => holeOutlineGeoms.forEach((g) => g.dispose()), [holeOutlineGeoms]);

  // While a drawing tool is active, panels should be raycast-transparent
  // so clicks/drags fall through to the ball beneath.
  const drawingActive = tool === 'polyline' || tool === 'freeform' || tool === 'shape';
  return (
    <group>
      <mesh
        geometry={fillGeom}
        {...(drawingActive ? { raycast: () => null } : {})}
        onClick={(e) => { e.stopPropagation(); onSelect && onSelect(p.id); }}
        onDoubleClick={(e) => {
          if (tool !== 'edit') return;
          e.stopPropagation();
          onEnterManip && onEnterManip(p.id);
        }}
        renderOrder={1}
      >
        <meshBasicMaterial
          color={p.color}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2}
        />
      </mesh>
      <line geometry={outlineGeom}>
        <lineBasicMaterial
          color={selectedId === p.id ? '#0EA5E9' : '#0F172A'}
          transparent
          opacity={selectedId === p.id ? 1 : 0.65}
          linewidth={2}
        />
      </line>
      {holeOutlineGeoms.map((g, hi) => (
        <line key={`hole-${hi}`} geometry={g}>
          <lineBasicMaterial
            color={selectedId === p.id ? '#0EA5E9' : '#0F172A'}
            transparent
            opacity={selectedId === p.id ? 1 : 0.65}
            linewidth={2}
          />
        </line>
      ))}
    </group>
  );
}

// Draggable + double-clickable handles on every corner of the currently
// selected panel. Left-click drag moves the vertex along the sphere.
// Double-click toggles the vertex's "smooth" flag → renders as a rounded
// corner (bezier arc across the two adjacent edges). Right-click deletes.
function VertexHandles({ panel, onDragEnd }) {
  const { gl, camera } = useThree();
  const beginEdit = useFreehand((s) => s.beginEdit);
  const moveVertex = useFreehand((s) => s.moveVertex);
  const toggleSmoothVertex = useFreehand((s) => s.toggleSmoothVertex);
  const insertVertexOnEdge = useFreehand((s) => s.insertVertexOnEdge);
  const removeVertex = useFreehand((s) => s.removeVertex);
  const snapEnabled = useFreehand((s) => s.snapEnabled);
  const snapToGrid = useFreehand((s) => s.snapToGrid);
  const gridType = useFreehand((s) => s.gridType);
  const gridDensity = useFreehand((s) => s.gridDensity);
  const allPanels = useFreehand((s) => s.panels);
  const handleScale = useFreehand((s) => s.handleScale);
  const draggingRef = useRef(null); // { index, moved }
  const clickTimers = useRef({});
  const smoothSet = new Set(panel.smooth || []);
  const HANDLE_LIFT = PANEL_LIFT + 0.008;
  // Handle sizes are scaled uniformly by the user-tweakable `handleScale`
  // so users can shrink them when they cover important geometry.
  const vertR = 0.026 * handleScale;
  const ringR0 = 0.028 * handleScale;
  const ringR1 = 0.036 * handleScale;
  const midR = 0.018 * handleScale;

  // Compute great-circle midpoint of each edge for the "+" handles.
  const edgeMids = panel.points.map((a, i) => {
    const b = panel.points[(i + 1) % panel.points.length];
    const m = slerp(a, b, 0.5);
    return [m[0], m[1], m[2]];
  });

  const raycast = useCallback((e) => {
    const rect = gl.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const r = new THREE.Raycaster();
    r.setFromCamera(ndc, camera);
    const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), BALL_RADIUS);
    const hit = new THREE.Vector3();
    if (!r.ray.intersectSphere(sphere, hit)) return null;
    const p = hit.clone().normalize();
    return [p.x, p.y, p.z];
  }, [gl, camera]);

  return (
    <group>
      {panel.points.map((v, i) => {
        const isSmooth = smoothSet.has(i);
        return (
          <mesh
            key={i}
            position={[v[0] * HANDLE_LIFT, v[1] * HANDLE_LIFT, v[2] * HANDLE_LIFT]}
            onPointerDown={(e) => {
              if (e.button !== 0) return; // ignore middle/right — those orbit
              e.stopPropagation();
              // Ctrl / ⌘ + click deletes the vertex (with symmetry propagation).
              // Falls through if the panel already only has the minimum
              // 3 vertices — `removeVertex` no-ops in that case.
              if (e.ctrlKey || e.metaKey) {
                removeVertex(panel.id, i);
                return;
              }
              e.target?.setPointerCapture?.(e.pointerId);
              draggingRef.current = { index: i, moved: false };
              beginEdit();
              gl.domElement.style.cursor = 'grabbing';
            }}
            onPointerMove={(e) => {
              if (!draggingRef.current || draggingRef.current.index !== i) return;
              e.stopPropagation();
              const p = raycast(e);
              if (!p) return;
              draggingRef.current.moved = true;
              // Snap against every panel — including this panel's own
              // symmetry siblings — but skip THIS exact vertex so a moving
              // handle can't latch onto its own current position. Also
              // consider grid intersections if Snap to Grid is on.
              const panelSnap = snapEnabled
                ? findSnap(p, allPanels, (5 * Math.PI) / 180, { panelId: panel.id, index: i })
                : null;
              const gridPts = (snapToGrid && gridType !== 'off') ? generateGridPoints(gridType, gridDensity) : null;
              const gridSnap = gridPts ? findGridSnap(p, gridPts, (5 * Math.PI) / 180) : null;
              const target = pickBestSnap(panelSnap, gridSnap);
              useFreehand.setState({ snapTarget: target, hoverPoint: p });
              moveVertex(panel.id, i, target ? target.point : p);
            }}
            onPointerUp={(e) => {
              if (!draggingRef.current || draggingRef.current.index !== i) return;
              e.stopPropagation();
              e.target?.releasePointerCapture?.(e.pointerId);
              const moved = draggingRef.current.moved;
              draggingRef.current = null;
              gl.domElement.style.cursor = 'auto';
              useFreehand.setState({ snapTarget: null, hoverPoint: null });
              if (!moved) {
                // A short click without motion — treat as a request for
                // single/double-click handling below.
                const t = clickTimers.current[i];
                if (t) {
                  clearTimeout(t);
                  delete clickTimers.current[i];
                  toggleSmoothVertex(panel.id, i);
                } else {
                  clickTimers.current[i] = setTimeout(() => {
                    delete clickTimers.current[i];
                  }, 280);
                }
              } else if (onDragEnd) onDragEnd();
            }}
            onPointerOver={() => { gl.domElement.style.cursor = 'grab'; }}
            onPointerOut={() => { if (!draggingRef.current) gl.domElement.style.cursor = 'auto'; }}
            renderOrder={2}
          >
            <sphereGeometry args={[vertR, 16, 12]} />
            <meshBasicMaterial color={isSmooth ? '#0EA5E9' : '#F8FAFC'} />
          </mesh>
        );
      })}
      {/* Solid outline handles get an outer ring so they read against light
          panels; smooth handles use a filled cyan disc to signal "rounded". */}
      {panel.points.map((v, i) => (
        <mesh
          key={`ring-${i}`}
          position={[v[0] * HANDLE_LIFT, v[1] * HANDLE_LIFT, v[2] * HANDLE_LIFT]}
          raycast={() => null}
          renderOrder={2}
        >
          <ringGeometry args={[ringR0, ringR1, 20]} />
          <meshBasicMaterial color="#0F172A" side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* Edge-midpoint "+" handles — click one to insert a new corner along
          that edge. Propagates through the symmetry group. */}
      {edgeMids.map((m, i) => (
        <mesh
          key={`mid-${i}`}
          position={[m[0] * HANDLE_LIFT, m[1] * HANDLE_LIFT, m[2] * HANDLE_LIFT]}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            insertVertexOnEdge(panel.id, i);
          }}
          onPointerOver={() => { gl.domElement.style.cursor = 'copy'; }}
          onPointerOut={() => { gl.domElement.style.cursor = 'auto'; }}
          renderOrder={2}
        >
          <sphereGeometry args={[midR, 12, 8]} />
          <meshBasicMaterial color="#22C55E" />
        </mesh>
      ))}
    </group>
  );
}

// Draggable handles for every hole ring on a punched panel. Amber styling
// distinguishes them from the outer boundary's white/cyan handles. Unlike
// the outer boundary, holes are per-panel (no symmetry propagation), so
// edits go through the store's `moveHoleVertex` / `insertHoleVertexOnEdge`
// helpers which touch only this panel.
function HoleHandles({ panel }) {
  const { gl, camera } = useThree();
  const moveHoleVertex = useFreehand((s) => s.moveHoleVertex);
  const insertHoleVertexOnEdge = useFreehand((s) => s.insertHoleVertexOnEdge);
  const deleteHoleVertex = useFreehand((s) => s.deleteHoleVertex);
  const _pushHistory = useFreehand((s) => s._pushHistory);
  const snapEnabled = useFreehand((s) => s.snapEnabled);
  const snapToGrid = useFreehand((s) => s.snapToGrid);
  const gridType = useFreehand((s) => s.gridType);
  const gridDensity = useFreehand((s) => s.gridDensity);
  const allPanels = useFreehand((s) => s.panels);
  const handleScale = useFreehand((s) => s.handleScale);
  const draggingRef = useRef(null); // { hi, vi, moved }
  const HANDLE_LIFT = PANEL_LIFT + 0.008;
  const vertR = 0.026 * handleScale;
  const ringR0 = 0.028 * handleScale;
  const ringR1 = 0.036 * handleScale;
  const midR = 0.018 * handleScale;

  const raycast = useCallback((e) => {
    const rect = gl.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const r = new THREE.Raycaster();
    r.setFromCamera(ndc, camera);
    const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), BALL_RADIUS);
    const hit = new THREE.Vector3();
    if (!r.ray.intersectSphere(sphere, hit)) return null;
    const p = hit.clone().normalize();
    return [p.x, p.y, p.z];
  }, [gl, camera]);

  const holes = panel.holes || [];
  if (!holes.length) return null;

  return (
    <group>
      {holes.map((hole, hi) => {
        // Normalise each vertex onto the unit sphere (safety) and compute
        // great-circle midpoints for the "+" insert handles.
        const ring = hole.map((v) => {
          const l = Math.hypot(v[0], v[1], v[2]) || 1;
          return [v[0] / l, v[1] / l, v[2] / l];
        });
        const edgeMids = ring.map((a, i) => slerp(a, ring[(i + 1) % ring.length], 0.5));
        return (
          <group key={`hole-${hi}`}>
            {/* Filled amber vertex handles — drag to reshape a hole */}
            {ring.map((v, vi) => (
              <mesh
                key={`hv-${hi}-${vi}`}
                position={[v[0] * HANDLE_LIFT, v[1] * HANDLE_LIFT, v[2] * HANDLE_LIFT]}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.stopPropagation();
                  // Ctrl / ⌘ + click deletes this hole vertex (min 3 kept).
                  if (e.ctrlKey || e.metaKey) {
                    deleteHoleVertex(panel.id, hi, vi);
                    return;
                  }
                  e.target?.setPointerCapture?.(e.pointerId);
                  draggingRef.current = { hi, vi, moved: false };
                  _pushHistory();
                  gl.domElement.style.cursor = 'grabbing';
                }}
                onPointerMove={(e) => {
                  const d = draggingRef.current;
                  if (!d || d.hi !== hi || d.vi !== vi) return;
                  e.stopPropagation();
                  const p = raycast(e);
                  if (!p) return;
                  d.moved = true;
                  const panelSnap = snapEnabled
                    ? findSnap(p, allPanels, (5 * Math.PI) / 180, null)
                    : null;
                  const gridPts = (snapToGrid && gridType !== 'off')
                    ? generateGridPoints(gridType, gridDensity)
                    : null;
                  const gridSnap = gridPts ? findGridSnap(p, gridPts, (5 * Math.PI) / 180) : null;
                  const target = pickBestSnap(panelSnap, gridSnap);
                  useFreehand.setState({ snapTarget: target, hoverPoint: p });
                  moveHoleVertex(panel.id, hi, vi, target ? target.point : p);
                }}
                onPointerUp={(e) => {
                  const d = draggingRef.current;
                  if (!d || d.hi !== hi || d.vi !== vi) return;
                  e.stopPropagation();
                  e.target?.releasePointerCapture?.(e.pointerId);
                  draggingRef.current = null;
                  gl.domElement.style.cursor = 'auto';
                  useFreehand.setState({ snapTarget: null, hoverPoint: null });
                }}
                onPointerOver={() => { gl.domElement.style.cursor = 'grab'; }}
                onPointerOut={() => { if (!draggingRef.current) gl.domElement.style.cursor = 'auto'; }}
                renderOrder={2}
              >
                <sphereGeometry args={[vertR, 16, 12]} />
                <meshBasicMaterial color="#F59E0B" />
              </mesh>
            ))}
            {/* Darker outline ring so amber handles read against amber holes */}
            {ring.map((v, vi) => (
              <mesh
                key={`hvr-${hi}-${vi}`}
                position={[v[0] * HANDLE_LIFT, v[1] * HANDLE_LIFT, v[2] * HANDLE_LIFT]}
                raycast={() => null}
                renderOrder={2}
              >
                <ringGeometry args={[ringR0, ringR1, 20]} />
                <meshBasicMaterial color="#78350F" side={THREE.DoubleSide} />
              </mesh>
            ))}
            {/* Edge-midpoint "+" handles — click to insert a new corner */}
            {edgeMids.map((m, ei) => (
              <mesh
                key={`hm-${hi}-${ei}`}
                position={[m[0] * HANDLE_LIFT, m[1] * HANDLE_LIFT, m[2] * HANDLE_LIFT]}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.stopPropagation();
                  insertHoleVertexOnEdge(panel.id, hi, ei);
                }}
                onPointerOver={() => { gl.domElement.style.cursor = 'copy'; }}
                onPointerOut={() => { gl.domElement.style.cursor = 'auto'; }}
                renderOrder={2}
              >
                <sphereGeometry args={[midR, 12, 8]} />
                <meshBasicMaterial color="#EA580C" />
              </mesh>
            ))}
          </group>
        );
      })}
    </group>
  );
}

// Little indicator drawn at the current snap target so users know the next
// polyline click (or freeform release) will lock onto a shared corner/edge
// or grid intersection.
function SnapIndicator() {
  const target = useFreehand((s) => s.snapTarget);
  const tool = useFreehand((s) => s.tool);
  if (!target || (tool !== 'polyline' && tool !== 'freeform' && tool !== 'edit')) return null;
  const p = target.point;
  const lift = PREVIEW_LIFT + 0.003;
  const isVertex = target.kind === 'vertex';
  const isGrid = target.kind === 'grid';
  const color = isGrid ? '#EC4899' : isVertex ? '#0EA5E9' : '#F59E0B';
  return (
    <group position={[p[0] * lift, p[1] * lift, p[2] * lift]}>
      <mesh raycast={() => null}>
        <sphereGeometry args={[isVertex ? 0.007 : 0.005, 12, 8]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <mesh raycast={() => null}>
        <ringGeometry args={[0.00875, 0.0125, 20]} />
        <meshBasicMaterial color={color} transparent opacity={0.5} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// Render the currently-drawn stroke plus its live symmetry preview.
function ActiveStrokes({ activePoints, pendingPoints, symmetryMode, ringN, symmetryMirror }) {
  const previews = useMemo(() => {
    const src = activePoints || pendingPoints;
    if (!src || src.length < 2) return [];
    // For live preview we don't clean/resample — just replicate the raw path.
    return replicateStroke(src, symmetryMode, ringN, symmetryMirror);
  }, [activePoints, pendingPoints, symmetryMode, ringN, symmetryMirror]);

  return (
    <group>
      {previews.map((r, i) => (
        <line key={i} geometry={outlineGeometry(r.points, PREVIEW_LIFT)}>
          <lineBasicMaterial color="#EF4444" transparent opacity={i === 0 ? 1 : 0.6} linewidth={2} />
        </line>
      ))}
      {pendingPoints && pendingPoints.length > 0 && (
        <group>
          {pendingPoints.map((p, i) => (
            <mesh key={i} position={[p[0] * PREVIEW_LIFT, p[1] * PREVIEW_LIFT, p[2] * PREVIEW_LIFT]} raycast={() => null}>
              <sphereGeometry args={[0.00375, 10, 6]} />
              <meshBasicMaterial color="#0F172A" />
            </mesh>
          ))}
        </group>
      )}
    </group>
  );
}

// Canvas-level drag → slide-panel controller. Active only when a panel is in
// manipulate mode AND manipMode === 'move'. Cast rays against the ball and
// call movePanel(from → to). Scale/Rotate are handled with buttons in the
// overlay pill so users don't need to invent gestures for them.
function PanelManipController() {
  const { camera, gl } = useThree();
  const manipPanelId = useFreehand((s) => s.manipPanelId);
  const manipMode = useFreehand((s) => s.manipMode);
  const movePanel = useFreehand((s) => s.movePanel);
  const exitManip = useFreehand((s) => s.exitManip);
  const _pushHistory = useFreehand((s) => s._pushHistory);
  const lastDir = useRef(null);

  useEffect(() => {
    if (!manipPanelId || manipMode !== 'move') return;
    const canvas = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), BALL_RADIUS);
    const hitDir = (evt) => {
      const rect = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((evt.clientX - rect.left) / rect.width) * 2 - 1,
        -((evt.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      const hit = new THREE.Vector3();
      if (!raycaster.ray.intersectSphere(sphere, hit)) return null;
      const n = hit.clone().normalize();
      return [n.x, n.y, n.z];
    };
    const onDown = (evt) => {
      if (evt.button !== 0) return;
      const d = hitDir(evt);
      if (!d) return;
      lastDir.current = d;
      _pushHistory();
      canvas.style.cursor = 'grabbing';
    };
    const onMove = (evt) => {
      if (!lastDir.current) return;
      const d = hitDir(evt);
      if (!d) return;
      movePanel(manipPanelId, lastDir.current, d);
      lastDir.current = d;
    };
    const onUp = () => {
      lastDir.current = null;
      canvas.style.cursor = 'grab';
    };
    const onKey = (evt) => { if (evt.key === 'Escape') exitManip(); };
    canvas.style.cursor = 'grab';
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      canvas.style.cursor = 'auto';
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [manipPanelId, manipMode, camera, gl, movePanel, exitManip, _pushHistory]);

  return null;
}

function DrawingLayer({ selectedId, setSelectedId }) {
  const tool = useFreehand((s) => s.tool);
  const activeStroke = useFreehand((s) => s.activeStroke);
  const pendingPolyline = useFreehand((s) => s.pendingPolyline);
  const activeShape = useFreehand((s) => s.activeShape);
  const shapeKind = useFreehand((s) => s.shapeKind);
  const panels = useFreehand((s) => s.panels);
  const symmetryMode = useFreehand((s) => s.symmetryMode);
  const ringN = useFreehand((s) => s.ringN);
  const symmetryMirror = useFreehand((s) => s.symmetryMirror);
  const ballOpacity = useFreehand((s) => s.ballOpacity);
  const referenceImage = useFreehand((s) => s.referenceImage);
  const referenceHidden = useFreehand((s) => s.referenceHidden);
  const showPanelNumbers = useFreehand((s) => s.showPanelNumbers);
  const beginFreeform = useFreehand((s) => s.beginFreeform);
  const extendFreeform = useFreehand((s) => s.extendFreeform);
  const finishFreeform = useFreehand((s) => s.finishFreeform);
  const polylineAddPoint = useFreehand((s) => s.polylineAddPoint);
  const polylineCommit = useFreehand((s) => s.polylineCommit);
  const beginShape = useFreehand((s) => s.beginShape);
  const updateShape = useFreehand((s) => s.updateShape);
  const finishShape = useFreehand((s) => s.finishShape);
  const deletePanel = useFreehand((s) => s.deletePanel);
  const updateHover = useFreehand((s) => s.updateHover);
  const clearHover = useFreehand((s) => s.clearHover);

  const { gl, camera } = useThree();
  const drawingRef = useRef(false);
  const clickTimerRef = useRef(null);

  // Raycast the pointer against a virtual unit sphere at origin. This works
  // even when the R3F event's `event.point` isn't set — we recompute the hit
  // directly from client coords.
  const raycastSphere = useCallback((event) => {
    const rect = gl.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, camera);
    const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), BALL_RADIUS);
    const hit = new THREE.Vector3();
    if (!raycaster.ray.intersectSphere(sphere, hit)) return null;
    const p = hit.clone().normalize();
    return [p.x, p.y, p.z];
  }, [gl, camera]);

  const handlePointerDown = useCallback((event) => {
    if (tool === 'freeform') {
      event.stopPropagation();
      event.target?.setPointerCapture?.(event.pointerId);
      const p = raycastSphere(event);
      if (!p) return;
      drawingRef.current = true;
      beginFreeform(p);
    } else if (tool === 'shape') {
      event.stopPropagation();
      event.target?.setPointerCapture?.(event.pointerId);
      const p = raycastSphere(event);
      if (!p) return;
      drawingRef.current = true;
      beginShape(p);
    }
  }, [tool, raycastSphere, beginFreeform, beginShape]);

  const handlePointerMove = useCallback((event) => {
    const p = raycastSphere(event);
    // Broadcast hover so the snap indicator + guides can update.
    if (tool === 'polyline' || tool === 'freeform' || tool === 'edit' || tool === 'shape') updateHover(p);
    if (tool === 'freeform' && drawingRef.current) {
      event.stopPropagation();
      if (p) extendFreeform(p);
    } else if (tool === 'shape' && drawingRef.current) {
      event.stopPropagation();
      if (p) updateShape(p);
    }
  }, [tool, raycastSphere, extendFreeform, updateHover, updateShape]);

  const handlePointerUp = useCallback((event) => {
    if (tool === 'freeform' && drawingRef.current) {
      event.stopPropagation();
      drawingRef.current = false;
      event.target?.releasePointerCapture?.(event.pointerId);
      finishFreeform();
    } else if (tool === 'shape' && drawingRef.current) {
      event.stopPropagation();
      drawingRef.current = false;
      event.target?.releasePointerCapture?.(event.pointerId);
      finishShape();
    }
  }, [tool, finishFreeform, finishShape]);

  const handleClick = useCallback((event) => {
    if (tool !== 'polyline') return;
    event.stopPropagation();
    const p = raycastSphere(event);
    if (!p) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      polylineCommit();
    } else {
      clickTimerRef.current = setTimeout(() => {
        polylineAddPoint(p);
        clickTimerRef.current = null;
      }, 220);
    }
  }, [tool, raycastSphere, polylineAddPoint, polylineCommit]);

  useEffect(() => () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
  }, []);

  // Ball-mesh event routing:
  // - freeform/polyline/shape: full pointer routing (draw + hover).
  // - edit: only pointer move (for hover-based guide lines). Clicks/drags
  //   fall through to the panels + handles beneath.
  // - erase: ignore everything on the raw ball.
  // - manipulate mode: ball must not intercept drags — canvas-level
  //   controller drives movePanel. Ball becomes fully transparent to events.
  const manipPanelId = useFreehand((s) => s.manipPanelId);
  const disabled = tool === 'erase' || tool === 'edit' || !!manipPanelId;
  const moveDisabled = tool === 'erase' || !!manipPanelId;

  return (
    <group>
      {referenceImage && !referenceHidden && <ReferenceBall image={referenceImage} />}
      {symmetryMode !== 'none' && symmetryMode !== 'antipodal' && (
        <AxisGuides mode={symmetryMode} ringN={ringN} />
      )}
      {showPanelNumbers && <PanelNumbers panels={panels} />}
      <mesh
        onPointerDown={disabled ? undefined : handlePointerDown}
        onPointerMove={moveDisabled ? undefined : handlePointerMove}
        onPointerUp={disabled ? undefined : handlePointerUp}
        onClick={disabled ? undefined : handleClick}
        onPointerOver={() => {
          if (tool === 'freeform' || tool === 'polyline' || tool === 'shape') gl.domElement.style.cursor = 'crosshair';
          else if (tool === 'erase') gl.domElement.style.cursor = 'not-allowed';
          else if (tool === 'edit') gl.domElement.style.cursor = 'default';
        }}
        onPointerOut={() => { gl.domElement.style.cursor = 'auto'; clearHover(); }}
      >
        <sphereGeometry args={[BALL_RADIUS, 96, 64]} />
        <meshStandardMaterial
          color="#F8FAFC"
          roughness={0.85}
          metalness={0.02}
          transparent
          opacity={ballOpacity}
          depthWrite={ballOpacity > 0.9}
        />
      </mesh>
      <GridMarkers />
      <CommittedPanels
        panels={panels}
        selectedId={selectedId}
        onSelect={(id) => {
          if (tool === 'erase') deletePanel(id);
          else if (tool === 'edit') setSelectedId(id);
          else setSelectedId(id);
        }}
        onEnterManip={(id) => {
          useFreehand.getState()._pushHistory();
          useFreehand.getState().setManipPanel(id);
          setSelectedId(id);
        }}
      />
      {tool === 'edit' && selectedId && (() => {
        const sel = panels.find((p) => p.id === selectedId);
        if (!sel) return null;
        return (
          <>
            <VertexHandles panel={sel} />
            <HoleHandles panel={sel} />
          </>
        );
      })()}
      <ActiveStrokes
        activePoints={activeStroke?.points || null}
        pendingPoints={pendingPolyline?.points || null}
        symmetryMode={symmetryMode}
        ringN={ringN}
        symmetryMirror={symmetryMirror}
      />
      {activeShape && activeShape.radius > 0.005 && (() => {
        const { rectW, rectH } = useFreehand.getState();
        const pts = buildShapePolygon(activeShape.center, activeShape.radius, shapeKind, rectW / rectH);
        return pts ? (
          <ActiveStrokes
            activePoints={pts.concat([pts[0]])}
            pendingPoints={null}
            symmetryMode={symmetryMode}
            ringN={ringN}
            symmetryMirror={symmetryMirror}
          />
        ) : null;
      })()}
      {activeShape && (
        <mesh position={[activeShape.center[0] * PREVIEW_LIFT, activeShape.center[1] * PREVIEW_LIFT, activeShape.center[2] * PREVIEW_LIFT]} raycast={() => null}>
          <sphereGeometry args={[0.02, 16, 12]} />
          <meshBasicMaterial color="#EF4444" />
        </mesh>
      )}
      <SnapIndicator />
      <GuideLines />
      <PanelManipController />
    </group>
  );
}

function SvgOverlay() {
  const draft = useFreehand((s) => s.svgDraft);
  const guides = useFreehand((s) => s.svgGuides);
  const lines = useMemo(() => {
    const out = guides.map((g) => ({ pts: g, color: '#0EA5E9', closed: false }));
    if (draft) {
      const opts = { center: SCALE_PIVOTS[draft.pivot], sizeRad: (draft.sizeDeg * Math.PI) / 180, spinRad: (draft.spinDeg * Math.PI) / 180 };
      draft.closed.forEach((sh) => {
        out.push({ pts: projectDecal(densify2d(sh.outer, true), opts), color: '#EF4444', closed: true });
        sh.holes.forEach((h) => out.push({ pts: projectDecal(densify2d(h, true), opts), color: '#EF4444', closed: true }));
      });
      draft.open.forEach((l) => out.push({ pts: projectDecal(densify2d(l, false), opts), color: '#0EA5E9', closed: false }));
    }
    return out;
  }, [draft, guides]);
  return (
    <group>
      {lines.map((l, i) => {
        const pts = l.pts.map((p) => [p[0] * PREVIEW_LIFT, p[1] * PREVIEW_LIFT, p[2] * PREVIEW_LIFT]);
        // eslint-disable-next-line react/no-array-index-key
        return <Line key={i} points={pts} color={l.color} lineWidth={l.closed ? 2 : 1.5} dashed={!l.closed} dashSize={0.02} gapSize={0.012} />;
      })}
    </group>
  );
}

function ScalePivotMarker() {
  const pivot = useFreehand((s) => s.scalePivot);
  const show = useFreehand((s) => s.scaleMarkerVisible || !!s.scaleAllSnapshot);
  if (!show) return null;
  const c = SCALE_PIVOTS[pivot];
  return (
    <group position={[c[0] * 1.03, c[1] * 1.03, c[2] * 1.03]}>
      <mesh renderOrder={20}>
        <sphereGeometry args={[0.035, 16, 16]} />
        <meshBasicMaterial color="#f97316" depthTest={false} transparent />
      </mesh>
      <mesh renderOrder={19}>
        <sphereGeometry args={[0.06, 16, 16]} />
        <meshBasicMaterial color="#f97316" opacity={0.3} depthTest={false} transparent />
      </mesh>
    </group>
  );
}

function CanvasCapture({ onReady }) {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    if (!onReady) return;
    onReady(() => {
      gl.render(scene, camera);
      return gl.domElement.toDataURL('image/png');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export default function FreehandViewport({ onReady, selectedId, setSelectedId }) {
  const tool = useFreehand((s) => s.tool);
  const manipPanelId = useFreehand((s) => s.manipPanelId);
  const manipMode = useFreehand((s) => s.manipMode);
  const setManipMode = useFreehand((s) => s.setManipMode);
  const exitManip = useFreehand((s) => s.exitManip);
  const scalePanel = useFreehand((s) => s.scalePanel);
  const rotatePanel = useFreehand((s) => s.rotatePanel);
  const _pushHistory = useFreehand((s) => s._pushHistory);
  const interactiveTool = tool === 'freeform' || tool === 'polyline' || tool === 'edit' || tool === 'shape' || !!manipPanelId;
  const nudgeScale = (f) => { _pushHistory(); scalePanel(manipPanelId, f); };
  const nudgeRotate = (a) => { _pushHistory(); rotatePanel(manipPanelId, a); };

  return (
    <div className="absolute inset-0 r3f-canvas" data-testid="freehand-viewport">
      <Canvas
        shadows
        dpr={[1, 2]}
        camera={{ position: [3.2, 2.2, 3.4], fov: 40 }}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
      >
        <color attach="background" args={['#F1F3F5']} />
        <StudioLights />
        <DrawingLayer selectedId={selectedId} setSelectedId={setSelectedId} />
        <ContactShadows position={[0, -1.35, 0]} opacity={0.35} scale={8} blur={2.4} far={3} />
        <OrbitControls
          makeDefault
          enablePan={false}
          minDistance={2.6}
          maxDistance={8}
          enableDamping
          dampingFactor={0.08}
          // While a drawing tool is active, LEFT click is used for drawing and
          // orbit is bound to RIGHT click so the ball can still be rotated.
          mouseButtons={
            interactiveTool
              ? { LEFT: null, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE }
              : { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN }
          }
        />
        <SvgOverlay />
        <ScalePivotMarker />
        <CanvasCapture onReady={onReady} />
      </Canvas>
      {manipPanelId && (
        <div
          className="absolute top-20 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-2 rounded-full text-sm font-medium text-slate-800 shadow-lg border border-slate-200"
          data-testid="freehand-panel-manip-banner"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
          <span className="text-xs text-slate-600 font-mono">{manipPanelId}</span>
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-full p-0.5 ml-1" data-testid="freehand-panel-manip-mode">
            {[
              { id: 'move', label: 'Move' },
              { id: 'scale', label: 'Scale' },
              { id: 'rotate', label: 'Rotate' },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setManipMode(id)}
                data-testid={`freehand-btn-manip-${id}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  manipMode === id ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {manipMode === 'scale' && (
            <div className="flex items-center gap-1 ml-1">
              <button
                onClick={() => nudgeScale(0.9)}
                data-testid="freehand-btn-panel-shrink"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-700 text-sm"
                title="Shrink"
              >−</button>
              <button
                onClick={() => nudgeScale(1.1)}
                data-testid="freehand-btn-panel-grow"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-700 text-sm"
                title="Grow"
              >+</button>
            </div>
          )}
          {manipMode === 'rotate' && (
            <div className="flex items-center gap-1 ml-1">
              <button
                onClick={() => nudgeRotate(-Math.PI / 24)}
                data-testid="freehand-btn-panel-rotate-ccw"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-700 text-xs"
                title="Rotate −7.5°"
              >↺</button>
              <button
                onClick={() => nudgeRotate(Math.PI / 24)}
                data-testid="freehand-btn-panel-rotate-cw"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-700 text-xs"
                title="Rotate +7.5°"
              >↻</button>
            </div>
          )}
          <button
            onClick={exitManip}
            data-testid="freehand-btn-exit-manip"
            className="ml-1 px-2.5 py-1 rounded-full bg-slate-900 text-white text-xs"
          >Done</button>
        </div>
      )}
    </div>
  );
}
